import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LoadingMaskService } from '@app/core/services/loading-mask.service';
import { MediaService } from '@app/core/services/media.service';
import { MediaAsset } from '@app/core/models/media';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { NavbarStateService } from '@app/core/services/navbar-state.service';
import { ScrollConstants } from '@app/core/utils/scroll-constants';
import { SearchableField, SearchBarComponent } from '@app/shared/search-bar/search-bar.component';
import { SearchCriteria } from '@app/core/utils/search-criteria';
import { SessionService } from '@app/security/session.service';
import { SortableComponent } from '@app/core/sortable-component';
import { TitleService } from '@app/core/services/title.service';
import { VideoUtils } from '@app/core/utils/video-utils';
import { Permissions, PermissionService } from '@app/core/services/permission.service';
import { PageManager } from '@app/core/utils/page-manager';
import { VideoThumbnailService } from '@app/core/services/video-thumbnail.service';
import { FeatureService } from '@app/core/services/feature.service';
import { SearchBarComponent as SearchBarComponent_1 } from '../../shared/search-bar/search-bar.component';
import { ColumnSortIconComponent } from '../../shared/column-sort-icon/column-sort-icon.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { NgClass, DatePipe } from '@angular/common';
import { CopiedMessageComponent } from '../../shared/copied-message/copied-message.component';
import { AddMediaAssetComponent } from '../add-media-asset/add-media-asset.component';
import { EditMediaAssetDetailsComponent } from '../edit-media-asset-details/edit-media-asset-details.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../shared/message-dialog/message-dialog.component';

@Component({
    selector: 'app-media-list',
    templateUrl: './media-list.component.html',
    styleUrls: ['./media-list.component.scss'],
    standalone: true,
    imports: [SearchBarComponent_1, ColumnSortIconComponent, InfiniteScrollDirective, NgClass, CopiedMessageComponent, AddMediaAssetComponent, EditMediaAssetDetailsComponent, MessageDialogComponent_1, DatePipe]
})
export class MediaListComponent extends SortableComponent implements OnInit, OnDestroy {

  @ViewChild(MessageDialogComponent, { static: true }) messageDialog: MessageDialogComponent;
  @ViewChild(SearchBarComponent, { static: true }) searchBar: SearchBarComponent;
  mediaAssets: MediaAsset[] = [];
  pageManager: PageManager = new PageManager();
  searchCriteria: SearchCriteria;
  loaded = false;
  showAddAssetDialog = false;
  showAssetDetailsDialog = false;
  showUpdateAssetDialog = false;
  readonly limit = 20;
  readonly throttle = ScrollConstants.throttle;
  readonly scrollDistance = ScrollConstants.scrollDistance;
  showNoSearchFieldsMessage = false;
  offset = 0;
  videoUtils = VideoUtils;
  permissions = Permissions;
  searchableFields = [
    new SearchableField('Asset Name', 'shortname'),
    new SearchableField('Asset Type', 'asset_type'),
    new SearchableField('Asset ID', 'asset_id'),
    new SearchableField('Description', 'description'),
    new SearchableField('Created By', 'created_by_name')
  ];
  newMediaAsset: MediaAsset;
  mediaAssetToEdit: MediaAsset;

  constructor(private titleService: TitleService,
              public navbarStateService: NavbarStateService,
              public mediaService: MediaService,
              public sessionService: SessionService,
              public loadingMaskService: LoadingMaskService,
              private videoThumbnailService: VideoThumbnailService,
              private featureService: FeatureService,
              private ps: PermissionService) {
    super();
  }

  ngOnInit() {
    if (this.ps.checkPermissions(Permissions.media_asset_create)) {
      this.titleService.activate('Media & Branding', '', 'Add New Asset', 'fa-plus-circle', () => {
        this.openAddNewMediaAssetDialog();
      });
    } else {
      this.titleService.activate('Media & Branding');
    }

    this.reloadMediaAssets();
  }

  ngOnDestroy() {
    this.titleService.deactivate();
  }

  clearNewMediaAsset() {
    if (this.newMediaAsset) {

      if (this.searchCriteria) {
        this.searchBar.clearSearch();
      } else {
        this.mediaAssets.unshift(this.newMediaAsset);

        // Use an artificial page of offset -1 to hold newly created media assets
        let newAssetPage = this.pageManager.pages.get(-1);
        if (!newAssetPage) {
          newAssetPage = [];
        }
        newAssetPage.unshift(this.newMediaAsset);
        this.pageManager.pages.set(-1, newAssetPage);
      }

      this.newMediaAsset = undefined;
    }
  }

  fetchData() {
    this.pageManager = new PageManager();
    this.mediaAssets = [];
    this.loaded = false;
    this.offset = 0;
    this.getMediaAssets();
  }

  private getMediaAssets() {
    const offset = this.offset;

    this.mediaService.getAllMediaAssets(this.limit, this.offset, this.ordering, this.searchCriteria, this.offset !== 0).subscribe(
      assets => {
        this.pageManager.addPage(offset, assets);
        this.mediaAssets = this.pageManager.flattenPages();
        this.videoThumbnailService.setVideoThumbnailUrls(this.mediaAssets);
      },
      error => {
        if (error.status !== 401) {
          this.messageDialog.showMessage('Ooops...there was error loading media assets.');
        }
      },
      () => {
        this.loaded = true;
      }
    );
  }

  openAddNewMediaAssetDialog() {
    this.showAddAssetDialog = true;
  }

  onClearSearch() {
    this.showNoSearchFieldsMessage = false;
    this.searchCriteria = undefined;
    this.reloadMediaAssets();
  }

  onCloseAddMediaAssetDialog() {
    this.showAddAssetDialog = false;
  }

  onCloseMediaAssetDetailsDialog() {
    this.showAssetDetailsDialog = false;
    this.clearNewMediaAsset();
  }

  onCloseUpdateMediaAssetDialog() {
    this.showUpdateAssetDialog = false;
    this.showAssetDetailsDialog = true;
  }

  /**
   * The add-media-asset dialog stays open after a successful save (it shows
   * its own "uploaded successfully" state so the user can start another
   * upload or close it themselves). Add the new asset to the table directly,
   * the same way onMediaAssetDetailsUpdated() does, instead of reloading from
   * the search-backed list endpoint (see MediaService.getAllMediaAssets),
   * whose index can lag behind the write and would otherwise silently drop
   * the asset the user just uploaded.
   */
  onMediaAssetAdded(mediaAsset: MediaAsset) {
    // getMediaAssets() calls this for a normal list fetch; the optimistic
    // insert path needs it too, or a newly-added video sits with no
    // thumbnail until the next full reload.
    this.videoThumbnailService.setVideoThumbnailUrls([mediaAsset]);
    this.newMediaAsset = mediaAsset;
    this.clearNewMediaAsset();
  }

  onMediaAssetClick(mediaAsset: MediaAsset) {
    if (this.ps.checkPermissions(Permissions.media_asset_edit)) {
      this.mediaAssetToEdit = Object.assign(new MediaAsset(), mediaAsset);
      this.showAssetDetailsDialog = true;
    }
  }

  onMediaAssetDetailsUpdated(mediaAsset: MediaAsset) {
    this.showAssetDetailsDialog = false;
    this.clearNewMediaAsset(); // clearNewMediaAsset also adds NEW assets to the page manager.  
    this.replaceMediaAsset(mediaAsset); // asset must exist before you try to replace it.  hence, gotta run this 2nd
  }

  onMediaAssetUpdated(mediaAsset: MediaAsset) {
    this.replaceMediaAsset(mediaAsset);
    this.showUpdateAssetDialog = false;
    this.showAssetDetailsDialog = true;
  }

  onRequestSearch(searchCriteria: SearchCriteria) {
    this.showNoSearchFieldsMessage = false;
    this.searchCriteria = searchCriteria;
    if (this.searchCriteria.searchFields.length === 0) {
      this.showNoSearchFieldsMessage = true;
      this.pageManager = new PageManager();
      this.mediaAssets = [];
      this.offset = 0;
    } else {
      this.reloadMediaAssets();
    }
  }

  onScrollDown() {
    this.offset += this.limit;
    this.getMediaAssets();
  }

  onUpdateRequest() {
    this.showAssetDetailsDialog = false;
    this.showUpdateAssetDialog = true;
  }

  reloadMediaAssets() {
    this.loaded = false;
    this.pageManager = new PageManager();
    this.mediaAssets = [];
    this.offset = 0;
    this.getMediaAssets();
  }

  replaceMediaAsset(mediaAsset: MediaAsset) {
    this.pageManager.forEachPage(page => {
      const index = page.findIndex(m => m.asset_id === mediaAsset.asset_id);
      if (index !== -1) {
        page[index] = mediaAsset;
      }
    });
    this.mediaAssets = this.pageManager.flattenPages();
  }
}
